import { histogram, max as d3Max, mean, scaleLinear } from "d3";
import { compose, countBy, identity, map, mapObjIndexed } from "ramda";

import {
  AssessmentResult,
  IExerciseAnalytics,
  IPartIndex,
  ITopicsChartData,
  IUnitInstanceAnalytics,
  IUnitsAnalytics,
  IUnitsData,
  IUnitsDataBase,
  IUser,
  IUsersAnalytics,
} from "../../ts/App/Analytics/Common";
import {
  AnswerSummaryState,
  AnswerSummaryType,
  ResponseChoice,
  ResponseSummary,
  UserPartInstanceSummary,
} from "../App/Analytics/Common/types";
import {
  CourseComponentTag,
  CourseComponentType,
  DefaultNames,
  ICourseComponent,
  IExerciseContentType,
  ICourseInstruction,
  ICourseUnit,
  IFullCourse,
  isExerciseType,
  IQuestionContentType,
  ICourseLocalizedText,
} from "../components/course-author/types";
import { IAssessmentTotals } from "../components/pages/Analytics/AssessmentAnalytics/AssessmentsGraph";
import {
  exerciseTypeFormatter,
  IBrushChartData,
  IUnitInstanceAnalyticsGraphData,
  IUnitInstanceAnalyticsGraphDataBase,
  lessonsScaleFormatter,
  UnitInstanceAnalyticsGraphDataKey,
} from "../formatters/analytics";
import { average, isNullOrUndefined, objectMap } from "../utils";
import { LocaleNames, PreferredLocale } from "../utils/locales";

export interface IUnitInstanceStatistics {
  graphData: IUnitInstanceAnalyticsGraphData;
  filteredGraphData: IUnitInstanceAnalyticsGraphData;
  totalResponses: number;
  filteredTotalResponses: number;
  filteredTotalResponsesLocale: number;
}

interface IUserUnitInstanceAnalytics {
  analytics: IExerciseAnalytics[] | [];
  filteredByLocaleAnalytics: IExerciseAnalytics[] | [];
}

export interface ILessonsChartsInfo {
  name: string;
  students: number | null;
  filtered?: number | null;
}

export interface ITimeChartsInfo {
  "Average Time": number;
  name: string;
  filtered?: number;
}

export interface IUnitChartsInfo {
  lessons: ILessonsChartsInfo[];
  times: ITimeChartsInfo[];
  name: string;
  level: number;
}

interface ICompactUnitSummaryUIData {
  [unitSummaryId: string]: {
    lastSeen: number | null;
    teachings: number;
    time: number;
    assessments: number[][];
  };
}

interface ICourseInstanceSummaryUIData {
  [accountId: string]: {
    unitSummaryId: string;
    completed: boolean;
    resets: number;
    courseRevId: number;
  };
}

export function analyticsCompactUnitSummaryGetResponseToCompactUnitSummaryUIData(
  data: any,
  extra?: any
): ICompactUnitSummaryUIData {
  const res: ICompactUnitSummaryUIData = {};

  data[1].forEach((row: any[]) => {
    res[row[0]] = {
      assessments: row[5],
      lastSeen: row[3],
      teachings: row[6],
      time: row[4],
    };
  });
  return res;
}

export function analyticsCourseInstanceSummaryGetResponseToCourseInstanceSummaryUIData(
  data: any,
  extra?: any
): ICourseInstanceSummaryUIData {
  const res: ICourseInstanceSummaryUIData = {};

  data[1].forEach((row: any[]) => {
    res[row[1]] = {
      completed: row[4],
      courseRevId: row[3],
      resets: row[6],
      unitSummaryId: row[0],
    };
  });

  return res;
}

interface IUnitDetailedPart<T> {
  content: T;
  index: string;
  name: string;
  tag: CourseComponentType;
}

interface IUnitPart extends IPartIndex {
  exercises: Array<{ exercises: Array<IUnitDetailedPart<IExerciseContentType>>; name: string }>;
  level: number;
  localizedUnitName: ICourseLocalizedText[];
  name: string;
  questions: Array<IUnitDetailedPart<IQuestionContentType>>;
}

export function analyticsCourseFullGetResponseToUnits(data: IFullCourse, extra?: any): IUnitPart[] {
  const elems = data.state.content.value.root.children.elems;
  const units: IUnitPart[] = [];
  const getAllUnits = (level: number, suffix: string, courseComponents?: ICourseComponent[]) => {
    if (!courseComponents) {
      return;
    }
    const nonDeletedComponents = courseComponents.filter(
      (e: ICourseComponent) => e && !e.deleted
    ) as ICourseComponent[];
    if (nonDeletedComponents.length === 0) {
      return;
    }
    const englishElems = nonDeletedComponents
      .filter((ndc: ICourseComponent) => ndc.tag === CourseComponentTag.Unit)
      .map((e: ICourseUnit) => ({
        elem: e.content.title.elems.filter(el => el.locale === "en")[0],
        elems: e.content.children.elems,
        exercises: e.content.children.elems.filter(
          (el: ICourseInstruction) =>
            el.tag === CourseComponentTag.Instruction &&
            el.content.parts.elems.filter(p => isExerciseType(p.tag)).length !== 0
        ),
        index: e.id,
        localizedUnitName: e.content.title.elems,
        questions: e.content.test ? e.content.test.questionBank.elems : [],
      }));
    for (const el of englishElems) {
      const unitApiSuffix = `${suffix}.${el.index}`;
      const exercises = el.exercises.map((ex: ICourseInstruction) => {
        const name =
          ex.content.name.elems.length > 0 ? ex.content.name.elems.filter(e => e.locale === "en")[0].value.text : "";
        const unitPrefix = `.children.${ex.id}`;
        const exs = ex.content.parts.elems
          .filter(e => isExerciseType(e.tag))
          .map(e => ({
            content: e.content,
            index: `${unitApiSuffix}${unitPrefix}.parts.${e.id}`,
            name:
              CourseComponentType.Choice !== e.tag
                ? exerciseTypeFormatter(e.tag)
                : e.content.singleChoice === true
                ? `single ${exerciseTypeFormatter(e.tag)}`
                : `multiple ${exerciseTypeFormatter(e.tag)}`,
            tag: e.tag,
          }));
        return { exercises: exs, name };
      });
      units.push({
        exercises,
        index: unitApiSuffix,
        level,
        localizedUnitName:
          el.localizedUnitName.length === 0
            ? [{ id: 0, locale: PreferredLocale.en, value: { text: DefaultNames.Unit } }]
            : el.localizedUnitName,
        name: el.elem !== undefined ? el.elem.value.text : DefaultNames.Unit,
        questions: el.questions
          .filter(q => !q.deleted)
          .map(q => ({
            content: q.content,
            index: `${unitApiSuffix}.test.questionBank.${q.id}`,
            name:
              CourseComponentType.Choice !== q.tag
                ? exerciseTypeFormatter(q.tag)
                : q.content.singleChoice === true
                ? `single ${exerciseTypeFormatter(q.tag)}`
                : `multiple ${exerciseTypeFormatter(q.tag)}`,
            tag: q.tag,
          })),
      });
      getAllUnits(level + 1, `${unitApiSuffix}.children`, el.elems);
    }
  };
  getAllUnits(1, "children", elems);
  return units;
}

export const unitsInstanceAnalyticsFromPartInstanceSummary = (
  unitsPartInstanceSummary: UserPartInstanceSummary[]
): IUnitInstanceAnalytics => {
  const unitsInstanceAnalytics = {};
  const unitsPartInstanceSummaryAmount = unitsPartInstanceSummary.length;
  for (let i = 0; i < unitsPartInstanceSummaryAmount; i++) {
    const unitPartInstanceSummary = unitsPartInstanceSummary[i];
    const accountId = unitPartInstanceSummary[0];
    const analytics = [];
    for (const answers of unitPartInstanceSummary[1]) {
      const locale = answers[3];
      const status = answers[1];
      const answerTime = answers[2];

      const response = getSummaryResponse(answers[4]);
      analytics.push({ locale, status, answerTime, response });
    }
    unitsInstanceAnalytics[accountId] = { analytics };
  }
  return unitsInstanceAnalytics;
};

const getSummaryResponse = (response: ResponseSummary | null) => {
  if (response === null) {
    return null;
  }

  const [answerSummaryType, responseData] = response;
  if (answerSummaryType === AnswerSummaryType.Choice) {
    const [choiceId] = responseData as ResponseChoice;
    return choiceId;
  }

  return null;
};

export const analyticsCourseAssessmentTotals = (assessments: AssessmentResult[][]): IAssessmentTotals => {
  let totalAssessments = 0;
  let totalPreAssigmentsNotTaken = 0;
  let totalPreAssigmentsNotTakenPassedOnFirstPostAssessment = 0;
  let totalPreAssigmentsTaken = 0;
  let totalPreAssigmentsTakenPassedOnFirst = 0;
  for (const assessment of assessments) {
    if (assessment.length > 0) {
      totalAssessments++;
      const a = new Set(assessment);
      if (
        a.has(AssessmentResult.PreAssessmentPassed) ||
        a.has(AssessmentResult.PreAssessmentFailed) ||
        a.has(AssessmentResult.PreAssessmentAborted)
      ) {
        totalPreAssigmentsTaken++;
        if (!a.has(AssessmentResult.PostAssessmentPassed)) {
          totalPreAssigmentsTakenPassedOnFirst++;
        }
      }
      if (
        !a.has(AssessmentResult.PreAssessmentPassed) &&
        !a.has(AssessmentResult.PreAssessmentFailed) &&
        !a.has(AssessmentResult.PreAssessmentAborted)
      ) {
        totalPreAssigmentsNotTaken++;
        if (
          a.has(AssessmentResult.PostAssessmentPassed) &&
          !a.has(AssessmentResult.PostAssessmentFailed) &&
          !a.has(AssessmentResult.PostAssessmentAborted)
        ) {
          totalPreAssigmentsNotTakenPassedOnFirstPostAssessment++;
        }
      }
    }
  }
  return {
    totalAssessments,
    totalPreAssigmentsNotTaken,
    totalPreAssigmentsNotTakenPassedOnFirstPostAssessment,
    totalPreAssigmentsTaken,
    totalPreAssigmentsTakenPassedOnFirst,
  };
};

export const getValueTotalsFromUsersInAnalytics = (users: IUser[], analytics: IUsersAnalytics, key: string) => {
  let totalValue = 0;
  let averageValue = 0;
  let maxValue = -Infinity;
  let minValue = Infinity;
  if (users.length !== 0) {
    users.forEach(cu => {
      let value = 0;
      if (analytics[cu.accountId]) {
        value = analytics[cu.accountId][key];
      }
      totalValue += value;
      if (value > maxValue) {
        maxValue = value;
      }
      if (value < minValue) {
        minValue = value;
      }
    });
    averageValue = totalValue / users.length;
  } else {
    averageValue = 0;
    maxValue = 0;
    minValue = 0;
  }
  return { averageValue, maxValue, minValue };
};

export const getTimeTakenGraphData = (selected: Set<string>, usersAnalytics: IUsersAnalytics, users: IUser[]) => {
  // To transform data from miliseconds to minutes
  const timeModifier = 60 * 1000;

  const isFilterActive = selected.size > 0;

  const getTimeFromAnalytics = getTimeFromUser(usersAnalytics);
  const usersTime: number[] = [];
  for (const user of users) {
    const userTime = getTimeFromAnalytics(user) / timeModifier;
    usersTime.push(userTime);
  }
  // To avoid sharp peaks, we reduce the number of peaks when having few data points
  const minTik = 25;
  const maxTik = 100;
  const ticks = users.length < 10 ? minTik : maxTik;
  const maxUserTime = d3Max(usersTime) || 0;
  // Add 10% time to graph smoothens at the end, we increase the ratio when having few data points
  // to avoid overlapping with the export menu
  const minRatio = 0.1;
  const maxRatio = 0.2;
  const smoothening = users.length < 10 ? maxRatio : minRatio;
  const x = scaleLinear()
    .domain([0, maxUserTime + maxUserTime * smoothening])
    .range([500, 0]);
  const bins = histogram()
    .domain(x.domain() as [number, number])
    .thresholds(ticks)(usersTime);
  const filteredValues: number[] = [];
  if (isFilterActive) {
    for (const user of users) {
      if (selected.has(user.accountId)) {
        const userTime = getTimeFromAnalytics(user) / timeModifier;
        filteredValues.push(userTime);
      }
    }
  }
  const densityValues = kernelDensityEstimator(x.ticks(ticks))(usersTime);
  const filteredDensityValues = kernelDensityEstimator(x.ticks(ticks))(filteredValues);
  let maxRange = 0;
  const data: IBrushChartData[] = bins.map((range, i) => {
    const lowerBound = range.x0 || 0;
    const upperBound = range.x1 || 0;
    const densityModifier = isFilterActive ? 5 : 1;
    if (upperBound > maxRange) {
      maxRange = upperBound;
    }
    return {
      density: (densityValues[i][1] || 0) * densityModifier,
      students: usersTime.filter(n => n >= lowerBound && n < upperBound).length,
      time: upperBound * timeModifier - 1,
      ...(isFilterActive && {
        filtered: filteredValues.filter(n => n >= lowerBound && n <= upperBound).length,
        filteredDensity: filteredDensityValues[i][1],
      }),
    };
  });
  maxRange = maxRange * timeModifier - 1;
  return { data, maxRange };
};

const kernelDensityEstimator = (X: number[]) => (V: number[]) =>
  X.map(x => [x, mean(V, (v: number) => kernelEpanechnikov(7)(x - v))]);

const kernelEpanechnikov = (k: number) => (v: number) => (Math.abs((v /= k)) <= 1 ? (0.75 * (1 - v * v)) / k : 0);

const getTimeFromUser = (usersAnalytics: IUsersAnalytics) => (u: IUser) =>
  !usersAnalytics[u.accountId] ? 0 : usersAnalytics[u.accountId].time;

export const getTopicsChartData = (
  minTopics: number,
  maxTopics: number,
  users: IUser[],
  usersAnalytics: IUsersAnalytics,
  selectedUsers: Set<string>
) => {
  const getData: (usersForData: IUser[], usersAnalyticsForData: IUsersAnalytics) => ITopicsChartData[] = (
    chartUsers,
    chartUsersAnalytics
  ) =>
    compose(
      Object.values,
      mapObjIndexed((v, k) => ({ topics: Number(k), students: v })),
      countBy<number>(identity),
      map((u: IUser) => chartUsersAnalytics[u.accountId].teachings)
    )(chartUsers);

  const addSelectedBarCharts: (
    users: IUser[],
    userAnalytics: IUsersAnalytics,
    data: ITopicsChartData[]
  ) => ITopicsChartData[] = (filteredUsers, allAnalytics, allData) => {
    const filteredData = getData(filteredUsers, allAnalytics);

    return allData.map(d => {
      const aux = filteredData.find(filtered => filtered.topics === d.topics);
      d.filtered = aux ? aux.students : 0;
      return d;
    });
  };
  const data =
    selectedUsers.size > 0
      ? addSelectedBarCharts(
          users.filter(u => selectedUsers.has(u.accountId)),
          usersAnalytics,
          getData(users, usersAnalytics)
        )
      : getData(users, usersAnalytics);

  return fillTopicsChartGraphDataWithEmptySpaces(data, minTopics, maxTopics);
};

export const fillTopicsChartGraphDataWithEmptySpaces = (
  graphData: ITopicsChartData[],
  minNumber: number,
  maxNumber: number
) => {
  const dataWithEmptySpaces = [];
  const getElemIfTopicExists = (topic: number) => {
    for (const d of graphData) {
      if (d.topics === topic) {
        return d;
      }
    }
    return { topics: topic, students: null };
  };
  for (let i = minNumber; i <= maxNumber; i++) {
    dataWithEmptySpaces.push(getElemIfTopicExists(i));
  }
  return dataWithEmptySpaces;
};

export const generateTopicsChartTicks = (graphData: ITopicsChartData[]) => {
  const ticks: number[] = [];
  graphData.forEach((gd, i) => {
    if (i % 2 === 0) {
      ticks.push(gd.topics);
    }
  });
  return ticks;
};

export const getUnitAnalyticsLessonsData = (
  users: IUser[],
  unitsAnalytics: IUnitsAnalytics,
  selected: Set<string>
): ILessonsChartsInfo[] => {
  const getData = (unitUsers: IUser[], unitAnalytics: IUnitsAnalytics): ILessonsChartsInfo[] =>
    compose(
      completeLessonsData,
      Object.values,
      mapObjIndexed<number, ILessonsChartsInfo>((v, k) => ({
        name: k,
        students: v,
      })),
      countBy<number>(lessonsScaleFormatter),
      map((u: IUser) => (!unitAnalytics.analytics[u.accountId] ? 0 : unitAnalytics.analytics[u.accountId].teachings))
    )(unitUsers);
  const addSelectedBarCharts = (
    filteredUsers: IUser[],
    unitAnalytics: IUnitsAnalytics,
    allData: ILessonsChartsInfo[]
  ): ILessonsChartsInfo[] => {
    const filteredData = getData(filteredUsers, unitAnalytics);
    return allData.map(d => {
      const aux = filteredData.find(filtered => filtered.name === d.name);
      d.filtered = aux ? aux.students : 0;
      return d;
    });
  };

  const completeLessonsData = (unitLessons: ILessonsChartsInfo[]): ILessonsChartsInfo[] => {
    const range = Array.from({ length: 8 }, (_, k) => k);
    return range.map(numberOfLessons => {
      const stringName = lessonsScaleFormatter(numberOfLessons);
      const entry = unitLessons.find(({ name }) => name === stringName);
      return entry ? entry : { name: stringName, students: null };
    });
  };

  return selected.size > 0
    ? addSelectedBarCharts(
        users.filter(u => selected.has(u.accountId)),
        unitsAnalytics,
        getData(users, unitsAnalytics)
      )
    : getData(users, unitsAnalytics);
};

export const getUnitAnalyticsAverageTimesData = (
  users: IUser[],
  unitsAnalytics: IUnitsAnalytics,
  selected: Set<string>
): ITimeChartsInfo[] => {
  const getData = (unitUsers: IUser[], unitAnalytics: IUnitsAnalytics): ITimeChartsInfo => ({
    "Average Time": Math.round(
      average(
        unitUsers.map(u =>
          !unitAnalytics.analytics[u.accountId] ? 0 : unitAnalytics.analytics[u.accountId].time / 1000
        )
      )
    ),
    name: unitAnalytics.name,
  });

  const addSelectedBarCharts = (
    filteredUsers: IUser[],
    unitAnalytics: IUnitsAnalytics,
    currentData: ITimeChartsInfo
  ): ITimeChartsInfo => {
    const filteredData = getData(filteredUsers, unitAnalytics);
    currentData.filtered = filteredData["Average Time"];
    return currentData;
  };

  return [
    selected.size > 0
      ? addSelectedBarCharts(
          users.filter(u => selected.has(u.accountId)),
          unitsAnalytics,
          getData(users, unitsAnalytics)
        )
      : getData(users, unitsAnalytics),
  ];
};

interface AnswerUnitType {
  correct: number;
  incorrect: number;
  skipped: number;
}

export const calculateUnitInstanceAnalyticsGraphData = (
  name: string,
  identifier: string,
  unitsInstanceAnalytics: IUnitInstanceAnalytics,
  users: IUser[],
  usersCourseAnalytics: IUsersAnalytics,
  selectedUsers: Set<string>,
  searchLocales?: [PreferredLocale, PreferredLocale]
): IUnitInstanceStatistics => {
  let totalResponses = 0;
  let correctAnswers = 0;
  let incorrectAnswers = 0;
  let skippedAnswers = 0;
  let filteredTotalResponses = 0;
  let filteredTotalResponsesLocale = 0;
  let filteredCorrectAnswers = 0;
  let filteredCorrectAnswersLocale = 0;
  let filteredIncorrectAnswers = 0;
  let filteredIncorrectAnswersLocale = 0;
  let filteredSkippedAnswers = 0;
  let filteredSkippedAnswersLocale = 0;
  /* When a language selection filtered is applied, graphData corresponds to data for All
  Languages and filteredData corresponds to data for the selected language.

  When a filter is applied, but no language is selected, graphData corresponds to data for
  all users whilst filteredData corresponds to filtered users, both calculated using All 
  Languages. When a filter is applied and a language is selected, the behavior is the same, 
  but the data is calculated using the selected language.
  */

  const getUserUnitInstanceAnalytics = (searchUser: IUser): IUserUnitInstanceAnalytics => {
    const userAnalytics = usersCourseAnalytics[searchUser.accountId];
    if (!userAnalytics) {
      return { analytics: [], filteredByLocaleAnalytics: [] };
    }
    const userUnitInstanceAnalytics = unitsInstanceAnalytics[userAnalytics.unitSummaryId];
    if (!userUnitInstanceAnalytics) {
      return { analytics: [], filteredByLocaleAnalytics: [] };
    }
    const analytics = userUnitInstanceAnalytics.analytics;
    const filteredAnalytics: IExerciseAnalytics[] = [];

    if (!searchLocales) {
      return { analytics, filteredByLocaleAnalytics: [] };
    }

    const [selectedLocale, masterLocale] = searchLocales;
    const analyticsAmount = analytics.length;
    for (let i = 0; i < analyticsAmount; i++) {
      const a = analytics[i];
      if ((selectedLocale === masterLocale && a.locale === null) || selectedLocale === a.locale) {
        filteredAnalytics.push(a);
      }
    }

    if (selectedUsers.size > 0) {
      return { analytics: filteredAnalytics, filteredByLocaleAnalytics: [] };
    }

    return { analytics, filteredByLocaleAnalytics: filteredAnalytics };
  };
  for (const user of users) {
    const { analytics, filteredByLocaleAnalytics } = getUserUnitInstanceAnalytics(user);
    totalResponses += analytics.length;
    if (selectedUsers.has(user.accountId)) {
      filteredTotalResponses += analytics.length;
    }
    for (const answer of analytics) {
      if (AnswerSummaryState.Correct === answer.status) {
        correctAnswers += 1;
        if (selectedUsers.has(user.accountId)) {
          filteredCorrectAnswers += 1;
        }
      }
      if (AnswerSummaryState.Incorrect === answer.status) {
        incorrectAnswers += 1;
        if (selectedUsers.has(user.accountId)) {
          filteredIncorrectAnswers += 1;
        }
      }
      if (AnswerSummaryState.Skipped === answer.status) {
        skippedAnswers += 1;
        if (selectedUsers.has(user.accountId)) {
          filteredSkippedAnswers += 1;
        }
      }
    }

    if (searchLocales !== undefined) {
      const [selectedLocale, masterLocale] = searchLocales;
      filteredTotalResponsesLocale += filteredByLocaleAnalytics.length;
      for (const localeAnswer of filteredByLocaleAnalytics) {
        const isValidLocale =
          (selectedLocale === masterLocale && localeAnswer.locale === null) || selectedLocale === localeAnswer.locale;
        if (AnswerSummaryState.Correct === localeAnswer.status && isValidLocale) {
          filteredCorrectAnswersLocale += 1;
        }
        if (AnswerSummaryState.Incorrect === localeAnswer.status && isValidLocale) {
          filteredIncorrectAnswersLocale += 1;
        }
        if (AnswerSummaryState.Skipped === localeAnswer.status && isValidLocale) {
          filteredSkippedAnswersLocale += 1;
        }
      }
    }
  }

  const onlyLanguageFilterActive = selectedUsers.size === 0 && searchLocales !== undefined;

  const graphData: IUnitInstanceAnalyticsGraphData = {
    correct: correctAnswers / totalResponses || 0,
    identifier,
    incorrect: incorrectAnswers / totalResponses || 0,
    key: onlyLanguageFilterActive
      ? UnitInstanceAnalyticsGraphDataKey.AllLanguages
      : UnitInstanceAnalyticsGraphDataKey.AllUsers,
    name,
    skipped: skippedAnswers / totalResponses || 0,
    totalResponses,
  };

  const filteredGraphData: IUnitInstanceAnalyticsGraphData = onlyLanguageFilterActive
    ? {
        correct: filteredCorrectAnswersLocale / filteredTotalResponsesLocale || 0,
        identifier,
        incorrect: filteredIncorrectAnswersLocale / filteredTotalResponsesLocale || 0,
        key: UnitInstanceAnalyticsGraphDataKey.SelectedLanguages,
        name,
        skipped: filteredSkippedAnswersLocale / filteredTotalResponsesLocale || 0,
        totalResponses: filteredTotalResponsesLocale,
      }
    : {
        correct: filteredCorrectAnswers / filteredTotalResponses || 0,
        identifier,
        incorrect: filteredIncorrectAnswers / filteredTotalResponses || 0,
        key: UnitInstanceAnalyticsGraphDataKey.Filtered,
        name,
        skipped: filteredSkippedAnswers / filteredTotalResponses || 0,
        totalResponses: filteredTotalResponses,
      };

  return {
    filteredGraphData,
    filteredTotalResponses,
    filteredTotalResponsesLocale,
    graphData,
    totalResponses,
  };
};

export interface IIndividualAssessmentAnalytics {
  [key: string]: IUnitInstanceAnalyticsGraphData;
}

const calculateIndividualLocaleData = (
  users: IUser[],
  unitInstanceAnalytics: IUnitInstanceAnalytics,
  userCourseAnalytics: IUsersAnalytics,
  selectedUsers: Set<string>,
  locale: PreferredLocale,
  masterLocale: PreferredLocale
) => {
  const getUserUnitInstanceAnalytics = (user: IUser) => {
    const userAccount = user.accountId;
    const userAnalytics = userCourseAnalytics[userAccount];
    if (!userAnalytics) {
      return [];
    }
    const userUnitInstanceId = userAnalytics.unitSummaryId;
    const userUnitInstanceAnalytics = unitInstanceAnalytics[userUnitInstanceId];
    if (!userUnitInstanceAnalytics) {
      return [];
    }
    return userUnitInstanceAnalytics.analytics;
  };
  let correctAnswers = 0;
  let incorrectAnswers = 0;
  let skippedAnswers = 0;
  let filteredCorrectAnswers = 0;
  let filteredIncorrectAnswers = 0;
  let filteredSkippedAnswers = 0;
  const answersByQuestion = {};
  const filteredAnswersByQuestion = {};
  const totalUsers = users.length;
  for (let i = 0; i < totalUsers; i++) {
    const user = users[i];
    const analytics = getUserUnitInstanceAnalytics(user);
    for (const answer of analytics) {
      const answerLocale = answer.locale;
      const isCorrect = AnswerSummaryState.Correct === answer.status;
      if (isCorrect && isCorrectLocale(locale, answerLocale, masterLocale)) {
        correctAnswers += 1;
        if (!isNullOrUndefined(answer.response)) {
          for (const response of answer.response!) {
            isNullOrUndefined(answersByQuestion[response])
              ? (answersByQuestion[response!] = { correct: 1, incorrect: 0, skipped: 0 })
              : (answersByQuestion[response!].correct += 1);
          }
        }
        if (selectedUsers.has(user.accountId)) {
          filteredCorrectAnswers += 1;
          for (const response of answer.response!) {
            isNullOrUndefined(filteredAnswersByQuestion[response])
              ? (filteredAnswersByQuestion[response] = { correct: 1, incorrect: 0, skipped: 0 })
              : (filteredAnswersByQuestion[response].correct += 1);
          }
        }
      }
      const isIncorrect = AnswerSummaryState.Incorrect === answer.status;
      if (isIncorrect && isCorrectLocale(locale, answerLocale, masterLocale)) {
        incorrectAnswers += 1;
        if (!isNullOrUndefined(answer.response)) {
          for (const response of answer.response!) {
            isNullOrUndefined(answersByQuestion[response!])
              ? (answersByQuestion[response!] = { correct: 0, incorrect: 1, skipped: 0 })
              : (answersByQuestion[response!].incorrect += 1);
          }
        }
        if (selectedUsers.has(user.accountId)) {
          filteredIncorrectAnswers += 1;
          for (const response of answer.response!) {
            isNullOrUndefined(filteredAnswersByQuestion[response])
              ? (filteredAnswersByQuestion[response!] = { correct: 0, incorrect: 1, skipped: 0 })
              : (filteredAnswersByQuestion[response!].incorrect += 1);
          }
        }
      }
      const isSkipped = AnswerSummaryState.Skipped === answer.status;
      if (isSkipped && isCorrectLocale(locale, answerLocale, masterLocale)) {
        skippedAnswers += 1;
        if (selectedUsers.has(user.accountId)) {
          filteredSkippedAnswers += 1;
        }
      }
    }
  }

  const individualAnswerByQuestion = objectMap(answersByQuestion, (answer: AnswerUnitType) => {
    const total = answer.correct + answer.incorrect + answer.skipped;
    return {
      correct: answer.correct / total || 0,
      incorrect: answer.incorrect / total || 0,
      skipped: answer.skipped / total || 0,
      totalResponses: total,
    };
  });

  const individualFilteredAnswerByQuestion = objectMap(filteredAnswersByQuestion, (answer: AnswerUnitType) => {
    const total = answer.correct + answer.incorrect + answer.skipped;
    return {
      correct: answer.correct / total || 0,
      incorrect: answer.incorrect / total || 0,
      skipped: answer.skipped / total || 0,
      totalResponses: total,
    };
  });
  return {
    correctAnswers,
    filteredAnswersByQuestion,
    filteredCorrectAnswers,
    filteredIncorrectAnswers,
    filteredSkippedAnswers,
    incorrectAnswers,
    individualAnswerByQuestion,
    individualFilteredAnswerByQuestion,
    skippedAnswers,
  };
};

const isCorrectLocale = (
  locale: PreferredLocale,
  answerLocale: PreferredLocale | null,
  masterLocale: PreferredLocale
) => (answerLocale === null && locale === masterLocale) || locale === answerLocale;

export const calculateIndividualAssessmentGraphData = (
  users: IUser[],
  unitInstanceAnalytics: IUnitInstanceAnalytics,
  userCourseAnalytics: IUsersAnalytics,
  selectedUsers: Set<string>,
  locales: PreferredLocale[],
  selectedLocale: PreferredLocale | undefined,
  masterLocale: PreferredLocale
) => {
  const localesData = {};
  let totalResponses = 0;
  let totalFilteredResponses = 0;
  let correctResponses = 0;
  let correctFilteredResponses = 0;
  let incorrectResponses = 0;
  let incorrectFilteredResponses = 0;
  let skippedResponses = 0;
  let skippedFilteredResponses = 0;
  const answersByQuestion = {};
  const filteredAnswersByQuestion = {};
  const totalLocales = locales.length;
  for (let i = 0; i < totalLocales; i++) {
    const locale = locales[i];
    const {
      correctAnswers,
      filteredCorrectAnswers,
      filteredIncorrectAnswers,
      filteredSkippedAnswers,
      incorrectAnswers,
      individualAnswerByQuestion,
      individualFilteredAnswerByQuestion,
      skippedAnswers,
    } = calculateIndividualLocaleData(
      users,
      unitInstanceAnalytics,
      userCourseAnalytics,
      selectedUsers,
      locale,
      masterLocale
    );
    totalResponses += correctAnswers + incorrectAnswers + skippedAnswers;
    totalFilteredResponses += filteredCorrectAnswers + filteredIncorrectAnswers + filteredSkippedAnswers;
    correctResponses += correctAnswers;
    correctFilteredResponses += filteredCorrectAnswers;
    incorrectResponses += incorrectAnswers;
    incorrectFilteredResponses += filteredIncorrectAnswers;
    skippedResponses += skippedAnswers;
    skippedFilteredResponses += filteredSkippedAnswers;

    const transformAnswers = (answers: object, individualAnswers: object) => {
      const answerIds = Object.keys(individualAnswers);
      answerIds.forEach(elem => {
        if (isNullOrUndefined(answers[elem])) {
          answers[elem] = individualAnswers[elem];
        } else {
          const total = answers[elem].totalResponses + individualAnswers[elem].totalResponses;
          const recalculate = (key: keyof IUnitInstanceAnalyticsGraphDataBase) =>
            (answers[elem][key] * answers[elem].totalResponses +
              individualAnswers[elem][key] * individualAnswers[elem].totalResponses) /
            total;
          answers[elem] = {
            correct: recalculate("correct"),
            incorrect: recalculate("incorrect"),
            skipped: recalculate("skipped"),
            totalResponses: total,
          };
        }
      });
    };
    transformAnswers(answersByQuestion, individualAnswerByQuestion);
    transformAnswers(filteredAnswersByQuestion, individualFilteredAnswerByQuestion);

    localesData[locale] = {
      correctAnswers,
      filteredCorrectAnswers,
      filteredIncorrectAnswers,
      filteredSkippedAnswers,
      incorrectAnswers,
      individualAnswerByQuestion,
      individualFilteredAnswerByQuestion,
      skippedAnswers,
    };
  }
  const graphsData: IIndividualAssessmentAnalytics = {};
  const filteredGraphsData: IIndividualAssessmentAnalytics = {};
  const totalKey = "Total";
  const onlyLanguageFilterActive = selectedUsers.size === 0 && selectedLocale !== undefined;
  graphsData[totalKey] = {
    answersByQuestion,
    correct: correctResponses / totalResponses || 0,
    identifier: "All users",
    incorrect: incorrectResponses / totalResponses || 0,
    key: onlyLanguageFilterActive
      ? UnitInstanceAnalyticsGraphDataKey.AllLanguages
      : UnitInstanceAnalyticsGraphDataKey.AllUsers,
    locale: "total",
    name: totalKey,
    skipped: skippedResponses / totalResponses || 0,
    totalResponses,
  };
  filteredGraphsData[totalKey] = {
    correct: 0,
    identifier: "No Filter",
    incorrect: 0,
    key: UnitInstanceAnalyticsGraphDataKey.Unfiltered,
    skipped: 0,
    totalResponses: 0,
  };

  if (selectedUsers.size > 0) {
    filteredGraphsData[totalKey] = {
      answersByQuestion: filteredAnswersByQuestion,
      correct: correctFilteredResponses / totalFilteredResponses || 0,
      identifier: "Filtered users",
      incorrect: incorrectFilteredResponses / totalFilteredResponses || 0,
      key: UnitInstanceAnalyticsGraphDataKey.Filtered,
      locale: "total",
      name: totalKey,
      skipped: skippedFilteredResponses / totalFilteredResponses || 0,
      totalResponses: totalFilteredResponses,
    };
  }

  for (let i = 0; i < totalLocales; i++) {
    const locale = locales[i];
    const localeAnalytics = localesData[locale];
    const name = LocaleNames[locale];
    const localeAndUsersFilterActive = selectedUsers.size > 0 && selectedLocale !== undefined;
    graphsData[name] = {
      answersByQuestion: localeAnalytics.individualAnswerByQuestion,
      correct: localeAnalytics.correctAnswers / totalResponses || 0,
      identifier: "All users",
      incorrect: localeAnalytics.incorrectAnswers / totalResponses || 0,
      key: localeAndUsersFilterActive
        ? UnitInstanceAnalyticsGraphDataKey.AllUsers
        : UnitInstanceAnalyticsGraphDataKey.SelectedLanguages,
      locale,
      name,
      skipped: localeAnalytics.skippedAnswers / totalResponses || 0,
      totalResponses,
    };
    filteredGraphsData[name] = {
      correct: 0,
      identifier: "No Filter",
      incorrect: 0,
      key: UnitInstanceAnalyticsGraphDataKey.Unfiltered,
      skipped: 0,
      totalResponses: 0,
    };

    if (onlyLanguageFilterActive) {
      filteredGraphsData[name] = {
        answersByQuestion: localeAnalytics.individualAnswerByQuestion,
        correct: localeAnalytics.correctAnswers / totalResponses || 0,
        identifier: "Selected Language",
        incorrect: localeAnalytics.incorrectAnswers / totalResponses || 0,
        key: UnitInstanceAnalyticsGraphDataKey.SelectedLanguages,
        locale,
        name,
        skipped: localeAnalytics.skippedAnswers / totalResponses || 0,
        totalResponses,
      };
    }

    // this is the info we take when filter and locale filter are applied
    if (selectedUsers.size > 0) {
      filteredGraphsData[name] = {
        answersByQuestion: localeAndUsersFilterActive
          ? localeAnalytics.individualFilteredAnswerByQuestion
          : filteredAnswersByQuestion,
        correct: localeAnalytics.filteredCorrectAnswers / totalResponses || 0,
        identifier: "Filtered users",
        incorrect: localeAnalytics.filteredIncorrectAnswers / totalResponses || 0,
        key: UnitInstanceAnalyticsGraphDataKey.Filtered,
        locale,
        name,
        skipped: localeAnalytics.filteredSkippedAnswers / totalResponses || 0,
        totalResponses,
      };
    }
  }
  return [graphsData, filteredGraphsData];
};

export const getUnitAnalytics = (elem: IUnitInstanceAnalyticsGraphData, name: string): IUnitsData => {
  const identifier = elem.identifier;
  const totals = elem.totalResponses;
  const isAnswerByQuestionNotEmpty = Object.keys(elem.answersByQuestion ?? {}).length > 0;
  let answersByQuestion;
  const amountAndPercentageCalcTotal = (value: number, total: number) => ({
    amount: Math.round(value * total),
    percentage: Math.round(value * 100),
  });
  const questionValues = {
    Correct: amountAndPercentageCalcTotal(elem.correct, elem.totalResponses),
    "Didn't Know": amountAndPercentageCalcTotal(elem.skipped, elem.totalResponses),
    Incorrect: amountAndPercentageCalcTotal(elem.incorrect, elem.totalResponses),
    identifier,
    locale: elem.locale ?? "total",
    name,
    totals,
  };

  if (isAnswerByQuestionNotEmpty) {
    answersByQuestion = calculateAttemptsGraphData(elem.answersByQuestion!, questionValues);
  }

  return {
    answersByQuestion,
    ...questionValues,
  };
};

export const calculateAttemptsGraphData = (
  answersByQuestion: Record<string, IUnitInstanceAnalyticsGraphDataBase>,
  totalValues: IUnitsDataBase
) => {
  const amountAndPercentageCalc = (value: number, totalPerQuestion: number, totalAll: number) => ({
    amount: Math.round(value * totalPerQuestion),
    percentage: Math.round(((value * totalPerQuestion) / totalAll) * 100),
  });

  return objectMap(answersByQuestion!, (answer: IUnitInstanceAnalyticsGraphDataBase) => {
    const totalResponses =
      totalValues.Correct.amount + totalValues.Incorrect.amount + totalValues["Didn't Know"].amount;
    const correct = amountAndPercentageCalc(answer.correct, answer.totalResponses, totalResponses);
    const incorrect = amountAndPercentageCalc(answer.incorrect, answer.totalResponses, totalResponses);
    const skipped = totalValues["Didn't Know"];
    const isAnswerCorrect = correct.amount > 0;

    return {
      Correct: isAnswerCorrect
        ? correct
        : {
            amount: 0,
            percentage: 0,
          },
      "Didn't Know": skipped,
      Incorrect: !isAnswerCorrect
        ? incorrect
        : {
            amount: 0,
            percentage: 0,
          },
      totals: totalResponses,
    };
  });
};
